import React, { useState } from "react";

import { getFileUrl } from "../../../../core/config/api";
import ListingBrandDot from "../../../../listings/components/ListingBrandDot";
import "../../../../listings/components/rc-listing-card.scss";
import "./select-listing-modal.scss";
import { getBaseCurrency, symbolFor } from "../../../../core/currency";

const formatPrice = (listing) => {
  const raw = listing.listing_price ?? listing.price ?? listing.sell_price;
  if (raw == null) return null;
  const amount = (raw / 100).toLocaleString("en-GB", {
    maximumFractionDigits: 0,
  });
  // Shared symbol table (no local map); absent currency → store base.
  return `${symbolFor(listing.currency ?? getBaseCurrency())}${amount}`;
};

const resolveBrand = (product) =>
  product?.brand?.name ||
  (typeof product?.brand === "string" ? product.brand : null);

const resolveModel = (product) =>
  product?.model?.name ||
  (typeof product?.model === "string" ? product.model : null);

const SelectableListingCard = ({
  listing,
  selectedId,
  currentListingId,
  productId,
  onSelect,
}) => {
  // Same onError-state pattern as order/components/ItemProduct/ProductImage.jsx:
  // `/assets/placeholder-watch.png` doesn't exist in public/, so the old
  // fallback 404'd to index.html. No image / a 404 now renders no <img> at
  // all rather than a broken icon. Declared before the early return below so
  // hook order stays stable (React forbids an early return above a hook).
  const [coverBroken, setCoverBroken] = useState(false);
  const listingProductId =
    listing?.product?.id ?? listing?.product_id ?? null;
  if (productId && listingProductId && listingProductId !== productId) {
    return null;
  }
  const isSelected = selectedId === listing.id;
  const isCurrent = currentListingId === listing.id;
  const product = listing.product || {};
  const brand = resolveBrand(product);
  const model = resolveModel(product);
  const reference = product?.reference_number;
  const cover =
    (listing.image_id && getFileUrl(listing.image_id)) ||
    (product?.images?.[0]?.id && getFileUrl(product.images[0].id));
  const price = formatPrice(listing);
  const reserved = !!listing.is_reserved;
  const consignment = !!(listing.consignment || listing.is_consignment);

  return (
    <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
      <div
        className={`rc-lcard select-listing-card${
          isSelected ? " is-selected" : ""
        }${isCurrent ? " is-current" : ""}`}
        onClick={() => onSelect(listing)}
      >
        <div className="select-listing-card__checkbox">
          <span className="select-listing-card__checkmark" aria-hidden="true">
            {isSelected ? "✓" : ""}
          </span>
        </div>
        {isCurrent && (
          <span className="select-listing-card__current-badge">Current</span>
        )}

        <div className="rc-lcard__info">
          <div className="rc-lcard__meta-row">
            {brand && <span className="rc-lcard__meta-item">{brand}</span>}
            {reference && brand && (
              <span className="rc-lcard__meta-item">|</span>
            )}
            {reference && (
              <span className="rc-lcard__meta-item">{reference}</span>
            )}
          </div>
          <div className="rc-lcard__name-row">
            <div className="rc-lcard__name-left">
              <ListingBrandDot brandName={brand} />
              {consignment && <span className="rc-lcard__cons-dot" />}
              <span className="rc-lcard__model">{model || "—"}</span>
            </div>
            <div className="rc-lcard__price-row">
              <span
                className={`rc-lcard__price${
                  !price ? " rc-lcard__price--por" : ""
                }`}
              >
                {price || "Price on Request"}
              </span>
            </div>
          </div>
          <div className="rc-lcard__meta-row">
            <span className="rc-lcard__meta-item rc-lcard__meta-item--year">
              {listing.year || "—"}
            </span>
            <span className="rc-lcard__meta-item">|</span>
            <span className="rc-lcard__meta-item">
              {listing.condition || "—"}
            </span>
          </div>
        </div>

        <div className="rc-lcard__image-area">
          {cover && !coverBroken && (
            <img
              src={cover}
              alt={model || ""}
              className="rc-lcard__image rc-lcard__image--clickable"
              onError={() => setCoverBroken(true)}
              onClick={(e) => {
                e.stopPropagation();
                window.open(
                  `/product/${listing.id}/edit/product`,
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
            />
          )}
          {reserved && (
            <span className="rc-lcard__reserved-badge">Reserved</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectableListingCard;
